import { faPlus, faRotateLeft, faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Checkbox,
  Flex,
  InlineAlert,
  Text,
  TextButton,
  TextLink,
} from '@modusign/design-system';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocalStorage } from 'react-use';

import { useSelectionValues } from '@hooks';

import {
  AuthorizedResourceRoleSummary,
  PermissionAssigneeSearchInput,
  ResourceRoleLabel,
} from '@app/features/authorization';
import { getUserID } from '@app/store/user/user/selectors';

import { LabelResourceRoleSubject } from '../../types';

type SelectionKey = `MEMBER:${string}` | `GROUP:${string}` | `WORKSPACE:${string}`;

type SummaryCount = { member: number; group: number };

function getSelectionKey(role: LabelResourceRoleSubject): SelectionKey {
  return `${role.subjectType}:${role.subjectId}` as const;
}

function countSummaryFromSelectionKeys(key: SelectionKey[]): SummaryCount {
  return {
    member: key.filter((k) => k.startsWith('MEMBER')).length,
    group: key.filter((k) => k.startsWith('GROUP')).length,
  };
}

interface LabelViewerTabPanelProps {
  roles: LabelResourceRoleSubject[];
  onAddClick: () => void;
  onRemove: (roles: LabelResourceRoleSubject[]) => void;
}

export default function LabelViewerTabPanel({
  roles,
  onAddClick,
  onRemove,
}: LabelViewerTabPanelProps) {
  const isPublicLabel = roles.some((r) => r.subjectType === 'WORKSPACE');
  const [searchKeyword, setSearchKeyword] = useState('');
  const { clear, selected, toggle } = useSelectionValues<SelectionKey>();

  const { visibleRoles, summaryCount, summaryPrefix } = useMemo<{
    visibleRoles: LabelResourceRoleSubject[];
    summaryCount: SummaryCount;
    summaryPrefix: string;
  }>(() => {
    const visibleRoles = searchKeyword
      ? roles.filter((role) => {
          if (
            (role.subjectType === 'GROUP' && role.groupName.includes(searchKeyword)) ||
            (role.subjectType === 'MEMBER' &&
              (role.memberEmail.includes(searchKeyword) || role.memberName.includes(searchKeyword)))
          ) {
            return true;
          }

          return false;
        })
      : roles;

    let summary: { prefix: string; count: { member: number; group: number } };
    if (selected.length) {
      summary = { prefix: '선택한 ', count: countSummaryFromSelectionKeys(selected) };
    } else {
      summary = {
        prefix: searchKeyword ? '검색 결과 : ' : '',
        count: countSummaryFromSelectionKeys(visibleRoles.map(getSelectionKey)),
      };
    }

    return {
      visibleRoles,
      summaryPrefix: summary.prefix,
      summaryCount: summary.count,
    };
  }, [roles, searchKeyword, selected]);

  if (isPublicLabel) {
    return (
      <>
        <LabelOnboardingInlineAlert />
        <Flex justifyContent="center" pb="60px" pt={9}>
          모든 멤버가 라벨을 보고 이용할 수 있습니다.
        </Flex>
      </>
    );
  }

  return (
    <>
      <LabelOnboardingInlineAlert />
      <Flex justifyContent="space-between" mb={6}>
        <Flex>
          {summaryPrefix && (
            <Text fontWeight="medium" whiteSpace="pre">
              {summaryPrefix}
            </Text>
          )}
          <AuthorizedResourceRoleSummary
            groupCount={summaryCount.group}
            isActive={selected.length !== 0}
            memberCount={summaryCount.member}
          />
        </Flex>
        {selected.length ? (
          <Flex gap={4}>
            <TextButton
              onClick={() => {
                onRemove(roles.filter((r) => selected.includes(getSelectionKey(r))));
                clear();
              }}
            >
              <FontAwesomeIcon icon={faTrashCan} />
              <Text ml="2px">삭제</Text>
            </TextButton>
            <TextButton onClick={clear}>
              <FontAwesomeIcon icon={faRotateLeft} />
              <Text ml="2px">선택 취소</Text>
            </TextButton>
          </Flex>
        ) : (
          <TextButton display={['none', null, 'block']} onClick={onAddClick}>
            <FontAwesomeIcon icon={faPlus} />
            <Text ml="2px">추가</Text>
          </TextButton>
        )}
      </Flex>
      <PermissionAssigneeSearchInput value={searchKeyword} onChange={setSearchKeyword} />
      <Box mt={8}>
        <Flex flexDirection="column" width="100%">
          <Flex borderBottom="1px solid" borderBottomColor="tableBorderNeutralBase" px={4} py={3}>
            <Text color="textNeutralLight" fontSize="sm">
              이름
            </Text>
          </Flex>
          <Flex flex="auto" flexDirection="column" height="350px" overflowY="auto">
            {visibleRoles.map((role) => {
              // subject type이 WORKSPACE인 role이 있을 경우 public label이므로 이 함수가 실행되지 않습니다. 타입 가드용 코드입니다.
              if (role.subjectType === 'WORKSPACE') {
                return null;
              }

              return (
                <Flex
                  alignItems="center"
                  borderBottom="1px solid"
                  borderBottomColor="tableBorderNeutralLight"
                  gap={4}
                  key={role.subjectId}
                  p={4}
                  width="100%"
                >
                  <Box display={['none', null, 'block']}>
                    <Checkbox
                      checked={selected.includes(getSelectionKey(role))}
                      onClick={() => toggle(getSelectionKey(role))}
                    />
                  </Box>
                  <ResourceRoleLabel role={role} />
                </Flex>
              );
            })}
          </Flex>
        </Flex>
      </Box>
    </>
  );
}

function LabelOnboardingInlineAlert() {
  const userId = useSelector(getUserID);
  const [isAlertChecked, setAlertChecked] = useLocalStorage(
    `modusign__label-inline-alert-checked-${userId}`,
    false
  );

  if (isAlertChecked) {
    return null;
  }

  return (
    <InlineAlert sx={{ mb: 8 }} variant="informational">
      내가 볼 수 있는 라벨이 적용되어도 공유받지 않은 데이터(문서·계약 등)는 볼 수 없습니다.
      데이터에 대한 접근 설정은 ‘공유’ 기능을 이용해 주세요.{' '}
      <TextLink
        href="https://support.modusign.co.kr/114456043101?hs_preview=KCazstWU-114456043101#:~:text=%EC%A0%95%EB%B3%B4%EC%97%90%EC%84%9C%20%ED%99%95%EC%9D%B8%EC%9D%B4%20%EA%B0%80%EB%8A%A5%ED%95%A9%EB%8B%88%EB%8B%A4.-,%F0%9F%93%8C%20%EB%AC%B8%EC%84%9C%20%EA%B3%B5%EC%9C%A0%20%EC%84%A4%EC%A0%95%ED%95%98%EA%B8%B0,-%E2%9C%94%20%EC%9E%90%EB%8F%99%20%EA%B3%B5%EC%9C%A0"
        target="_blank"
        onClick={() => setAlertChecked(true)}
      >
        자세히
      </TextLink>
    </InlineAlert>
  );
}
